import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Braces, History, MessageCircle } from "lucide-react"
import { useState } from "react"
import { Panel } from "@/components/layout"
import {
	AdminChatWidget,
	AdminNegotiationChatProvider,
	AdminNotesPanel,
	OfferHistory,
	OfferItemsTable,
	OfferPageHeader,
	OfferRail,
} from "@/components/marketplace"
import { JsonViewCard } from "@/components/marketplace/json-view-card"
import { ModerationReferenceBanner } from "@/components/moderation"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useNegotiationDetailChangeStream } from "@/queries/change-streams/use-negotiation-change-stream"
import { useAdminNegotiationDetail, useAdminNotes } from "@/queries/marketplace"

const TABS = ["details", "chat", "full-json"] as const
type OfferTab = (typeof TABS)[number]

export const Route = createFileRoute("/offers/$negotiationId/")({
	component: OfferDetailPage,
	validateSearch: createTabSearch(TABS),
})

const STREAM_CHAT_API_KEY = import.meta.env.VITE_STREAM_CHAT_API_KEY || ""

function OfferDetailPage() {
	const { negotiationId } = Route.useParams()
	const searchParams = Route.useSearch()
	const { negotiation, isLoading, error } = useAdminNegotiationDetail({
		negotiationId,
	})
	useNegotiationDetailChangeStream(negotiation?.id)
	const navigate = useNavigate()
	const activeTab = searchParams.tab ?? "details"

	const setActiveTab = (tab: string) => {
		navigate({
			to: "/offers/$negotiationId",
			params: { negotiationId },
			search: tab === "details" ? {} : { tab: tab as OfferTab },
			replace: true,
		})
	}
	const [notesOpen, setNotesOpen] = useState(false)
	const { total: notesTotal } = useAdminNotes({
		targetType: "negotiation",
		targetId: negotiation?.id ?? "",
		enabled: !!negotiation,
	})

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<Skeleton className="h-16 w-full" />
				<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
					<div className="space-y-4">
						<Skeleton className="h-64" />
						<Skeleton className="h-48" />
					</div>
					<div className="space-y-4">
						<Skeleton className="h-32" />
						<Skeleton className="h-64" />
					</div>
				</div>
			</div>
		)
	}

	if (error || !negotiation) {
		return (
			<div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-4 sm:p-6">
				<h2 className="text-lg font-semibold">Offer not found</h2>
				<p className="text-bad">
					{error?.message ||
						`The negotiation with ID ${negotiationId} could not be found.`}
				</p>
				<Button variant="outline" asChild>
					<Link to="/offers" search={{} as never}>
						<ArrowLeft />
						Back to offers
					</Link>
				</Button>
			</div>
		)
	}

	const showNotes = notesOpen || (notesTotal ?? 0) > 0

	const handleAddNote = () => {
		setNotesOpen(true)
		setActiveTab("details")
		setTimeout(() => {
			document
				.getElementById("admin-notes")
				?.scrollIntoView({ behavior: "smooth", block: "center" })
		}, 50)
	}

	return (
		<div className="px-4 pb-10 sm:px-6">
			<OfferPageHeader negotiation={negotiation} onAddNote={handleAddNote} />

			<ModerationReferenceBanner
				reference={{ negotiationId: negotiation.id }}
				className="mt-6"
			/>

			<div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="min-w-0">
					<Tabs
						value={activeTab}
						onValueChange={setActiveTab}
						className="gap-6"
					>
						<TabsList variant="line">
							<TabsTrigger value="details" className="flex items-center gap-2">
								<History />
								Details and history
							</TabsTrigger>
							<TabsTrigger value="chat" className="flex items-center gap-2">
								<MessageCircle />
								Chat log
							</TabsTrigger>
							<TabsTrigger
								value="full-json"
								className="flex items-center gap-2"
							>
								<Braces />
								Full JSON
							</TabsTrigger>
						</TabsList>

						<TabsContent value="details" className="mt-0">
							<div className="flex min-w-0 flex-col gap-4">
								{showNotes && (
									<div id="admin-notes" className="scroll-mt-28">
										<AdminNotesPanel
											targetType="negotiation"
											targetId={negotiation.id}
											variant="panel"
										/>
									</div>
								)}
								<OfferItemsTable negotiation={negotiation} />
								<OfferHistory negotiation={negotiation} />
							</div>
						</TabsContent>

						<TabsContent value="chat" className="mt-0">
							<Panel className="h-[calc(100vh-16rem)] min-h-[400px]">
								{STREAM_CHAT_API_KEY && activeTab === "chat" ? (
									<AdminNegotiationChatProvider
										apiKey={STREAM_CHAT_API_KEY}
										negotiationId={negotiation.id}
									>
										<AdminChatWidget
											channelId={negotiation.chatChannelId}
											participantName={`@${negotiation.buyer.username} ↔ @${negotiation.seller.username}`}
											notice={
												<>
													You are posting as <strong>CardNexus Support</strong>{" "}
													in the buyer ↔ seller negotiation thread. Both parties
													will see your message.
												</>
											}
										/>
									</AdminNegotiationChatProvider>
								) : (
									<p className="flex h-full items-center justify-center text-muted-foreground">
										{!STREAM_CHAT_API_KEY
											? "Chat API key not configured"
											: "Switch to this tab to load chat"}
									</p>
								)}
							</Panel>
						</TabsContent>

						<TabsContent value="full-json" className="mt-0">
							<JsonViewCard
								title="Full Negotiation JSON"
								description="Raw negotiation data for debugging"
								data={negotiation}
							/>
						</TabsContent>
					</Tabs>
				</div>

				<OfferRail negotiation={negotiation} />
			</div>
		</div>
	)
}
